import React, { useState } from 'react';

const HeatMapComponent = ({ data, xLabels, yLabels, title, colorScheme = 'default' }) => {
  const [hoveredCell, setHoveredCell] = useState(null);
  const isZeroValue = (value) => value != null && Number(value) === 0;
  const zeroDisabledStyle = {
    backgroundColor: '#e5e7eb',
    backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)',
    backgroundSize: '6px 6px',
  };
  const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);
  const interpolateColor = (start, end, ratio) => {
    const boundedRatio = clamp(ratio);
    const red = Math.round(start[0] + (end[0] - start[0]) * boundedRatio);
    const green = Math.round(start[1] + (end[1] - start[1]) * boundedRatio);
    const blue = Math.round(start[2] + (end[2] - start[2]) * boundedRatio);
    return `rgb(${red}, ${green}, ${blue})`;
  };
  const monthMap = {
    jan: 0, january: 0,
    feb: 1, february: 1,
    mar: 2, march: 2,
    apr: 3, april: 3,
    may: 4,
    jun: 5, june: 5,
    jul: 6, july: 6,
    aug: 7, august: 7,
    sep: 8, sept: 8, september: 8,
    oct: 9, october: 9,
    nov: 10, november: 10,
    dec: 11, december: 11,
  };
  const parseYearFromLabel = (label) => {
    if (typeof label !== 'string') return null;
    const trimmed = label.trim();
    if (!trimmed) return null;

    const monthYearMatch = trimmed.match(/^([A-Za-z]{3,9})\s+(\d{2}|\d{4})$/);
    if (monthYearMatch) {
      const monthToken = monthYearMatch[1].toLowerCase();
      if (monthMap[monthToken] == null) return null;

      const rawYear = Number(monthYearMatch[2]);
      const normalizedYear = monthYearMatch[2].length === 2
        ? (rawYear >= 70 ? 1900 + rawYear : 2000 + rawYear)
        : rawYear;

      return normalizedYear;
    }

    const direct = new Date(trimmed);
    if (!Number.isNaN(direct.getTime())) {
      return direct.getFullYear();
    }

    return null;
  };

  // Filter out null values for calculating min and max
  const allValues = data.flat().filter((val) => val != null);
  const hasZeroValues = allValues.some((value) => isZeroValue(value));

  if (allValues.length === 0) {
    return (
      <div className="w-full mb-8">
        <h2 className="text-2xl font-bold text-center mb-4">{title}</h2>
        <p className="text-center text-gray-600">No Data Available</p>
      </div>
    );
  }

  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues);

  const getColor = (value) => {
    if (value === null) return '#f3f4f6';  // Light gray for null values
    if (isZeroValue(value)) return '#cbd5e1'; // Greyed-out for exact zero values

    switch (colorScheme) {
      case 'beta': {
        const red = [255, 0, 0];
        const white = [255, 255, 255];
        const green = [0, 255, 0];

        if (value < 0) {
          const minNegative = Math.min(minValue, 0);
          const ratio = minNegative === 0 ? 1 : value / minNegative;
          return interpolateColor(white, red, ratio);
        }

        const maxPositive = Math.max(maxValue, 0);
        const ratio = maxPositive === 0 ? 1 : value / maxPositive;
        return interpolateColor(white, green, ratio);
      }

      case 'p-value':
        // Green (significant) to White (threshold) to Red (not significant)
        if (value <= 0.1) {
          const ratio = value / 0.1;  // 0 -> darkest green, 0.1 -> white
          const intensity = Math.floor(255 * ratio);
          return `rgb(${intensity}, 255, ${intensity})`;
        } else {
          const ratio = Math.min((value - 0.1) / 0.1, 1);  // 0.1 -> white, 0.2+ -> darkest red
          const intensity = Math.floor(255 * (1 - ratio));
          return `rgb(255, ${intensity}, ${intensity})`;
        }
      
      case 'white-to-blue':
        // White to Blue scale
        const blueRatio = maxValue === minValue ? 0 : (value - minValue) / (maxValue - minValue);
        const intensity = Math.floor(255 * (1 - blueRatio));
        return `rgb(${intensity}, ${intensity}, 255)`;
      
      default:
        // Default diverging scale (red-white-green)
        const mid = (maxValue + minValue) / 2;
        if (value > mid) {
          const ratio = (value - mid) / (maxValue - mid);
          const intensity = Math.floor(255 * ratio);
          return `rgb(${255 - intensity}, 255, ${255 - intensity})`;
        } else {
          const ratio = (mid - value) / (mid - minValue);
          const intensity = Math.floor(255 * ratio);
          return `rgb(255, ${255 - intensity}, ${255 - intensity})`;
        }
    }
  };

  const getCellStyle = (value) => {
    if (isZeroValue(value)) {
      return zeroDisabledStyle;
    }
    return { backgroundColor: getColor(value) };
  };

  // Format detailed value for hover
  const formatDetailedValue = (value, x, y) => {
    if (value === null) return 'No data';
    return `${yLabels[y]} (${xLabels[x]}): ${value.toFixed(3)}`;
  };

  const getLegendItems = () => {
    switch (colorScheme) {
      case 'beta':
        return [
          { color: 'rgb(255, 0, 0)', label: '< 0' },
          { color: 'rgb(0, 255, 0)', label: '> 0' }
        ];
      case 'p-value':
        return [
          { color: 'rgb(0, 255, 0)', label: 'p ≤ 0.1' },
          { color: 'rgb(255, 255, 255)', label: 'p = 0.1' },
          { color: 'rgb(255, 0, 0)', label: 'p > 0.1' }
        ];
      case 'white-to-blue':
        return [
          { color: 'rgb(255, 255, 255)', label: minValue.toFixed(2) },
          { color: 'rgb(128, 128, 255)', label: ((maxValue + minValue) / 2).toFixed(2) },
          { color: 'rgb(0, 0, 255)', label: maxValue.toFixed(2) }
        ];
      default:
        return [
          { color: 'rgb(255, 0, 0)', label: 'Low' },
          { color: 'rgb(255, 255, 255)', label: 'Mid' },
          { color: 'rgb(0, 255, 0)', label: 'High' }
        ];
    }
  };
  const baseLegendItems = getLegendItems();
  const legendItems = hasZeroValues
    ? [...baseLegendItems, { color: '#e5e7eb', label: 'Zero (disabled)' }]
    : baseLegendItems;

  const yearGroups = (() => {
    if (!xLabels.length) return [];

    const parsedYears = xLabels.map((label) => parseYearFromLabel(label));
    const hasAllYears = parsedYears.every((year) => year != null);

    if (!hasAllYears) {
      const fallbackGroups = [];
      for (let i = 0; i < xLabels.length; i += 12) {
        const endIndex = Math.min(i + 11, xLabels.length - 1);
        fallbackGroups.push({
          label: `Year ${Math.floor(i / 12) + 1}`,
          span: endIndex - i + 1,
        });
      }
      return fallbackGroups;
    }

    const groups = [];
    let startIdx = 0;

    for (let idx = 1; idx <= parsedYears.length; idx += 1) {
      const currentYear = parsedYears[startIdx];
      const nextYear = idx < parsedYears.length ? parsedYears[idx] : null;

      if (idx === parsedYears.length || nextYear !== currentYear) {
        groups.push({
          label: `${currentYear}`,
          span: idx - startIdx,
        });
        startIdx = idx;
      }
    }

    return groups;
  })();

  return (
    <div className="w-full mb-8 p-4 bg-white rounded-lg shadow-lg">
      {title && <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">{title}</h2>}
      
      <div className="relative w-full overflow-x-hidden">
        <div className="border border-gray-200 rounded-lg">
          {/* Main grid container */}
          <div className="grid w-full" style={{
            gridTemplateColumns: `minmax(110px, 1.4fr) repeat(${xLabels.length}, minmax(0, 1fr))`,
          }}>
            {/* Empty corner cell */}
            <div className="border-b border-r border-gray-200 h-14" />

            {/* Year-group headers */}
            {yearGroups.map((group, idx) => (
              <div
                key={`x-${idx}`}
                className="border-b border-r border-gray-200 h-14 px-2 flex items-center justify-center bg-gray-50"
                style={{ gridColumn: `span ${group.span}` }}
                title={group.label}
              >
                <div className="w-full text-center text-[11px] text-gray-700 font-semibold leading-tight truncate">
                  {group.label}
                </div>
              </div>
            ))}

            {/* Rows */}
            {data.map((row, y) => (
              <React.Fragment key={`row-${y}`}>
                {/* Y-axis label */}
                <div className="bg-white border-b border-r border-gray-200" title={yLabels[y]}>
                  <div className="h-10 flex items-center px-2 text-[11px] text-gray-700 font-medium leading-tight truncate">
                    {yLabels[y]}
                  </div>
                </div>

                {/* Data cells */}
                {row.map((value, x) => {
                  const isZero = isZeroValue(value);
                  const isInteractive = value != null && !isZero;
                  const isHovered = hoveredCell?.x === x && hoveredCell?.y === y;
                  return (
                    <div
                      key={`cell-${x}-${y}`}
                      className={`relative h-10 border-b border-r border-gray-200 transition-all duration-150 
                        ${isHovered && isInteractive ? 'ring-2 ring-blue-500 z-10' : ''}`}
                      style={getCellStyle(value)}
                      onMouseEnter={() => {
                        if (isInteractive) {
                          setHoveredCell({ x, y, value });
                        }
                      }}
                      onMouseLeave={() => setHoveredCell(null)}
                    >
                      {/* Hover tooltip */}
                      {isHovered && isInteractive && (
                        <div className="absolute z-20 -top-12 left-1/2 transform -translate-x-1/2 pointer-events-none
                          bg-gray-800 text-white px-3 py-2 rounded-lg text-xs whitespace-nowrap shadow-lg">
                          {formatDetailedValue(value, x, y)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex justify-center items-center space-x-6 text-xs text-gray-600">
        {legendItems.map((item, index) => (
          <div key={index} className="flex items-center">
            <div 
              className="w-4 h-4 rounded mr-2 border border-gray-200" 
              style={item.label === 'Zero (disabled)'
                ? zeroDisabledStyle
                : { backgroundColor: item.color }}
            />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HeatMapComponent;
